/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, {
    constructViewportInformationForImage,
    getImageResizeUrl,
    getLargestGridSettingViewportName,
    getMinMaxWidth,
    IAny,
    IGeneric,
    IViewPort,
    Viewport
} from '@msdyn365-commerce/core-internal';
import { cloneDeep as _cloneDeep } from 'lodash';
import {
    BackgroundImageRepeat,
    BackgroundImageSize,
    IBackgroundImageAttributes,
    IBackgroundImageLazyloadAttributes,
    IBackgroundImageProps,
    IBackgroundImageStyle
} from './container-background-image-interfaces';

const getBackgroundRepeatProperty = (size: string | undefined, repeat: string | undefined): BackgroundImageRepeat => {
    if (!size || size === 'cover') {
        return 'no-repeat';
    }
    switch (repeat) {
        case 'noRepeat':
            return 'no-repeat';
        case 'repeat':
            return 'repeat';
        default:
            return 'no-repeat';
    }
};

const getBackgroundSizeProperty = (size: string | undefined): BackgroundImageSize => {
    switch (size) {
        case 'cover':
            return 'cover';
        case 'contain':
            return 'contain';
        case 'default':
        default:
            return undefined;
    }
};

const getDefaultViewports = (): IViewPort => {
    return {
        xs: {
            w: 800,
            h: 600
        },
        sm: {
            w: 1200,
            h: 900
        },
        md: {
            w: 1600,
            h: 900
        },
        lg: {
            w: 1600,
            h: 700
        },
        xl: {
            w: 1600,
            h: 700
        }
    };
};

const getAuthoringViewport = (moduleName: string | undefined, layout: string | undefined): IViewPort | undefined => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const authoringHelper = (window as any)._msdyn365.authoringHelper;
    if (!authoringHelper) {
        return;
    }

    // if authoring helper is setup, component is rendering from site builder. Build viewport based on layout selected.
    return constructViewportInformationForImage(moduleName, layout, 'backgroundImage', authoringHelper?.themeSettings) as IViewPort;
};

export const buildBackgroundImageStylesByViewport = (props: IBackgroundImageProps): IGeneric<IBackgroundImageStyle> => {
    const { containerType, image, request, configs } = props;

    const gridSettings = request.gridSettings ?? {};
    const imageSettings = image.imageSettings;

    const styles: IGeneric<IBackgroundImageStyle> = {};

    const largestGridSettingViewportName = getLargestGridSettingViewportName(gridSettings);
    if (largestGridSettingViewportName && imageSettings) {
        // in site builder the layout may change, so use authoring viewports.
        const authoringViewPorts = getAuthoringViewport(containerType, configs?.layout);
        const imgSettings = {
            ...imageSettings,
            viewports: { ..._cloneDeep(authoringViewPorts || imageSettings.viewports || getDefaultViewports()) }
        };

        // Skip resizing width and height for background images.
        const skipWidthAndHeightSettings = true;
        Object.keys(imgSettings.viewports).map(vp => {
            const viewport = vp as Viewport;
            const urlSrc = getImageResizeUrl(image.src, imgSettings.viewports[viewport], imgSettings, skipWidthAndHeightSettings);
            const minMaxWidth = getMinMaxWidth(gridSettings, imgSettings, viewport, largestGridSettingViewportName);
            styles[viewport] = {
                maxWidth: minMaxWidth.max,
                minWidth: minMaxWidth.min,
                minHeight: imgSettings.viewports[viewport]?.h,
                backgroundImage: urlSrc,
                backgroundRepeat: getBackgroundRepeatProperty(configs?.size, configs?.repeat),
                backgroundSize: getBackgroundSizeProperty(configs?.size),
                backgroundPosition: 'center center'
            };
        });
    }

    return styles;
};

export const setStyleAttribute = (currentStyle: IBackgroundImageStyle): IGeneric<IAny> => {
    let style: IGeneric<IAny> = {};
    if (currentStyle) {
        style = { ...currentStyle };
        style['minWidth'] = undefined;
        style['width'] = '100%';
        if (currentStyle.backgroundImage) {
            style['backgroundImage'] = `url('${currentStyle.backgroundImage}')`;
        }
    }
    return style;
};

export const getBackgroundImageLazyloadAttributes = (
    className: string,
    currentStyle: IBackgroundImageStyle | undefined,
    styles: IGeneric<IBackgroundImageStyle>
): IBackgroundImageLazyloadAttributes => {
    const attributes = {
        role: 'img',
        className: `${className} lazyload`,
        'data-bgset': getDataBgSetAttribute(styles)
    };

    if (currentStyle) {
        attributes['style'] = setStyleAttribute(currentStyle);
        // On lazyload, lazysizes plugin should pick which background image to use based on data-bgset attribute.
        attributes['style']['backgroundImage'] = undefined;
    }
    return attributes;
};

export const getBackgroundImageAttributes = (
    className: string,
    currentStyle: IBackgroundImageStyle | undefined
): IBackgroundImageAttributes => {
    const attributes = {
        role: 'img',
        className
    };

    if (currentStyle) {
        attributes['style'] = setStyleAttribute(currentStyle);
    }
    return attributes;
};

export const getBgSetMinMaxWidthAttribute = (style: IBackgroundImageStyle): string => {
    if (style.maxWidth) {
        return `[(max-width: ${style.maxWidth}px)]`;
    }
    return '';
};

export const getDataBgSetAttribute = (styles: IGeneric<IBackgroundImageStyle>): string => {
    const dataBgSetAttributes: string[] = [];
    Object.keys(styles).map(vp => {
        if (styles[vp].backgroundImage) {
            dataBgSetAttributes.push(`${styles[vp].backgroundImage} ${getBgSetMinMaxWidthAttribute(styles[vp])}`);
        }
    });

    return dataBgSetAttributes.join(' | ');
};
